import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'blocknotify_cookie_consent';
const CONSENT_VERSION = '1'; // Increment if you need to re-ask consent

/**
 * Check if user has given cookie consent
 */
export const hasConsent = () => {
    try {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) return false;
        const parsed = JSON.parse(consent);
        return parsed.version === CONSENT_VERSION && parsed.accepted === true;
    } catch {
        return false;
    }
};

/**
 * Check if user has explicitly declined
 */
export const hasDeclined = () => {
    try {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) return false;
        const parsed = JSON.parse(consent);
        return parsed.version === CONSENT_VERSION && parsed.accepted === false;
    } catch {
        return false;
    }
};

/**
 * Check if user has made any choice (accept or decline)
 */
export const hasChoice = () => {
    try {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) return false;
        const parsed = JSON.parse(consent);
        return parsed.version === CONSENT_VERSION;
    } catch {
        return false;
    }
};

/**
 * Save consent choice
 */
const saveConsent = (accepted) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
        version: CONSENT_VERSION,
        accepted,
        timestamp: new Date().toISOString(),
    }));
};

/**
 * Initialize Google Analytics after consent
 */
export const initializeGA = () => {
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    
    if (!GA_ID || GA_ID === '' || GA_ID.includes('XXXXXXXXXX')) {
        console.debug('[Analytics] GA ID not configured');
        return;
    }

    // Check if already loaded
    if (window.gtag) {
        console.debug('[Analytics] GA already initialized');
        return;
    }

    // Load the gtag script dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(script);

    script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_ID, { send_page_view: true });
        console.debug('[Analytics] GA initialized after consent');
    };
};

/**
 * Cookie Consent Banner Component
 */
const CookieConsent = () => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Show banner if no choice has been made
        if (!hasChoice()) {
            // Small delay to not block initial render
            const timer = setTimeout(() => setVisible(true), 1000);
            return () => clearTimeout(timer);
        }
        
        // If already consented, initialize GA
        if (hasConsent()) {
            initializeGA();
        }
    }, []);

    const handleAccept = () => {
        saveConsent(true);
        setVisible(false);
        initializeGA();
    };

    const handleDecline = () => {
        saveConsent(false);
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div
            // bottom-14 on mobile: the mobile tab bar is fixed bottom-0 z-50 and
            // was rendering ON TOP of this banner, making Accept/Decline
            // unclickable on narrow windows. Sit above it (and above its z).
            className="fixed bottom-14 md:bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
            style={{ 
                background: 'linear-gradient(to top, rgba(15, 23, 42, 0.98), rgba(15, 23, 42, 0.95))',
                backdropFilter: 'blur(8px)',
                borderTop: '1px solid rgba(99, 102, 241, 0.2)',
            }}
        >
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                {/* Text */}
                <div className="flex-1 text-center md:text-left">
                    <p className="text-white font-medium text-sm md:text-base mb-1">
                        🍪 We use cookies
                    </p>
                    <p className="text-gray-400 text-sm">
                        We use cookies to improve your experience and analyze site usage.{' '}
                        <Link 
                            to="/privacy" 
                            className="text-indigo-400 hover:text-indigo-300 underline"
                        >
                            Learn more
                        </Link>
                    </p>
                </div>

                {/* Buttons */}
                <div className="flex gap-3 shrink-0">
                    <button
                        onClick={handleDecline}
                        className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white 
                                   border border-gray-600 hover:border-gray-500 rounded-lg
                                   transition-colors duration-200"
                    >
                        Decline
                    </button>
                    <button
                        onClick={handleAccept}
                        className="px-5 py-2 text-sm font-medium text-white 
                                   bg-indigo-600 hover:bg-indigo-500 rounded-lg
                                   transition-colors duration-200 shadow-lg shadow-indigo-500/25"
                    >
                        Accept
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CookieConsent;






